import { bridge } from '../bridge'

/// Copy `text` to the system clipboard, most-capable path first.
///
/// Three tiers, in descending order of how much control we have over what
/// happens to the bytes afterwards:
///
///   1. `clipboard.write` over the native bridge. The only tier that can
///      scope the write — iOS localOnly + expiry, Android EXTRA_IS_SENSITIVE.
///      That matters because the values we offer to copy (shell commands,
///      plugin args) routinely carry credentials, and an unscoped
///      UIPasteboard.general write lands on the user's laptop via Universal
///      Clipboard.
///   2. `navigator.clipboard.writeText`. Browsers, plus native shells older
///      than the bridge method — the web bundle deploys independently of the
///      app, so an installed shell that rejects `clipboard.write` as unknown
///      is expected, not an error.
///   3. `document.execCommand('copy')`. Pre-Clipboard-API WKWebView builds
///      and non-secure contexts.
///
/// Resolves true when some tier confirmed the write. Callers surface a false
/// by simply not showing "Copied" — a success flip over a clipboard that
/// didn't take is worse than no feedback at all.
export async function copyText(text: string): Promise<boolean> {
  if (!text) return false

  // Deliberately not attempted in a browser, even though the web bridge
  // implements the method: Safari drops transient user activation across an
  // `await`, so a round trip through the bridge would make the very API it
  // delegates to reject. Nothing is lost — the browser tier below IS what
  // the browser bridge would have called.
  if (bridge.isNative) {
    try {
      const { ok } = await bridge.call('clipboard.write', { text })
      if (ok) return true
    } catch {
      // Unknown method on an older shell, or the native write failed. Fall
      // through. Note the activation caveat above applies to the retry: on a
      // shell that predates the method, tier 2 may reject and tier 3 is what
      // actually copies.
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Insecure context or a gesture the UA didn't accept; try the old way.
    }
  }

  return legacyCopy(text)
}

/// Pre-Clipboard-API fallback: select an offscreen textarea and let the
/// document copy command lift it.
function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    // Copy just no-ops if even this fails; not worth surfacing an error.
    return false
  }
}
